<template>
  <!--
    Issue #44 — AccessibilityPanel.vue: opciones de accesibilidad (tareas 15.7–15.8)
    Panel flotante para controlar tamaño de fuente, alto contraste y animaciones.
  -->
  <div class="ap-container">
    <!-- Botón de apertura (tarea 15.5.3) -->
    <button
      ref="triggerRef"
      class="ap-trigger"
      type="button"
      :aria-expanded="open"
      aria-controls="ap-panel"
      aria-label="Opciones de accesibilidad"
      @click="open = !open"
    >
      <i class="pi pi-universal-access" aria-hidden="true" />
    </button>

    <!-- Panel de opciones (tarea 15.7) -->
    <Transition name="ap-slide">
      <div
        v-if="open"
        id="ap-panel"
        role="dialog"
        aria-modal="false"
        aria-labelledby="ap-panel-title"
        class="ap-panel"
        @keydown.esc="close"
      >
        <div class="ap-header">
          <h3 id="ap-panel-title" class="ap-title">
            <i class="pi pi-universal-access" aria-hidden="true" /> Accesibilidad
          </h3>
          <button
            class="ap-close"
            type="button"
            aria-label="Cerrar panel de accesibilidad"
            @click="close"
          >
            <i class="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        <!-- Tamaño de fuente (tarea 15.7.1) -->
        <section class="ap-section" aria-labelledby="ap-font-label">
          <p id="ap-font-label" class="ap-section-label">
            <i class="pi pi-text" aria-hidden="true" /> Tamaño de texto
          </p>
          <div class="ap-font-btns" role="radiogroup" aria-labelledby="ap-font-label">
            <button
              v-for="s in FONT_SIZES"
              :key="s.value"
              class="ap-font-btn"
              type="button"
              :class="{ 'ap-font-btn--active': prefs.fontSize === s.value }"
              :aria-pressed="prefs.fontSize === s.value"
              :aria-label="`Texto ${s.label}`"
              @click="setFontSize(s.value)"
            >
              <span :style="{ fontSize: s.previewSize }">A</span>
              <span class="ap-font-label">{{ s.label }}</span>
            </button>
          </div>
        </section>

        <!-- Alto contraste (tarea 15.7.2) -->
        <section class="ap-section" aria-labelledby="ap-contrast-label">
          <p id="ap-contrast-label" class="ap-section-label">
            <i class="pi pi-eye" aria-hidden="true" /> Contraste
          </p>
          <label class="ap-toggle">
            <input
              v-model="prefs.highContrast"
              type="checkbox"
              role="switch"
              :aria-checked="prefs.highContrast"
              aria-label="Activar modo de alto contraste"
            />
            <span class="ap-toggle-track" aria-hidden="true" />
            <span class="ap-toggle-label">Alto contraste</span>
          </label>
        </section>

        <!-- Reducir animaciones (tarea 15.7.3) -->
        <section class="ap-section" aria-labelledby="ap-motion-label">
          <p id="ap-motion-label" class="ap-section-label">
            <i class="pi pi-spin" aria-hidden="true" /> Animaciones
          </p>
          <label class="ap-toggle">
            <input
              v-model="prefs.reducedMotion"
              type="checkbox"
              role="switch"
              :aria-checked="prefs.reducedMotion"
              aria-label="Reducir animaciones y movimiento"
            />
            <span class="ap-toggle-track" aria-hidden="true" />
            <span class="ap-toggle-label">Reducir movimiento</span>
          </label>
        </section>

        <!-- Atajos de teclado (tarea 15.8.1) -->
        <section class="ap-section ap-shortcuts" aria-labelledby="ap-shortcuts-label">
          <p id="ap-shortcuts-label" class="ap-section-label">
            <i class="pi pi-search" aria-hidden="true" /> Atajos de teclado
          </p>
          <dl class="ap-shortcut-list">
            <div class="ap-shortcut-row">
              <dt><kbd>Ctrl</kbd>+<kbd>K</kbd></dt>
              <dd>Búsqueda global</dd>
            </div>
            <div class="ap-shortcut-row">
              <dt><kbd>Tab</kbd></dt>
              <dd>Siguiente elemento</dd>
            </div>
            <div class="ap-shortcut-row">
              <dt><kbd>Esc</kbd></dt>
              <dd>Cerrar diálogo</dd>
            </div>
          </dl>
        </section>

        <!-- Restablecer -->
        <button class="ap-reset" type="button" @click="resetPrefs">
          <i class="pi pi-refresh" aria-hidden="true" /> Restablecer valores
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAccessibility } from '@/composables/financial/useAccessibility'
import type { FontSize } from '@/composables/financial/useAccessibility'

const open = ref(false)
const triggerRef = ref<HTMLButtonElement | null>(null)

const { prefs, setFontSize, resetPrefs } = useAccessibility()

const FONT_SIZES: Array<{ value: FontSize; label: string; previewSize: string }> = [
  { value: 'small',  label: 'Pequeño', previewSize: '12px' },
  { value: 'medium', label: 'Normal',  previewSize: '16px' },
  { value: 'large',  label: 'Grande',  previewSize: '20px' },
]

function close() {
  open.value = false
  triggerRef.value?.focus()
}
</script>

<style scoped>
/* ── Contenedor flotante ── */
.ap-container { position: fixed; bottom: 1.25rem; left: 1.25rem; z-index: 8888; }

/* ── Botón trigger ── */
.ap-trigger {
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--primary-color, #6366f1); color: #fff;
  border: none; cursor: pointer; font-size: 1.2rem;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  transition: opacity 0.2s;
}
.ap-trigger:hover { opacity: 0.88; }
.ap-trigger:focus { outline: 3px solid var(--primary-color, #6366f1); outline-offset: 3px; }

/* ── Panel ── */
.ap-panel {
  position: absolute; bottom: 54px; left: 0;
  width: 280px; background: var(--surface-card, #fff);
  border: 1px solid var(--surface-d, #e5e7eb); border-radius: 0.75rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18); padding: 1rem;
  display: flex; flex-direction: column; gap: 0.875rem;
}

/* ── Cabecera ── */
.ap-header { display: flex; align-items: center; justify-content: space-between; }
.ap-title { font-size: 0.9rem; font-weight: 700; color: var(--text-color, #1e293b); display: flex; align-items: center; gap: 0.4rem; }
.ap-close { background: none; border: none; cursor: pointer; color: var(--text-color-secondary, #64748b); font-size: 0.85rem; padding: 0.2rem; border-radius: 0.25rem; }
.ap-close:focus { outline: 2px solid var(--primary-color, #6366f1); }

/* ── Sección ── */
.ap-section { display: flex; flex-direction: column; gap: 0.5rem; }
.ap-section-label { font-size: 0.78rem; font-weight: 600; color: var(--text-color-secondary, #64748b); display: flex; align-items: center; gap: 0.3rem; }

/* ── Fuentes ── */
.ap-font-btns { display: flex; gap: 0.5rem; }
.ap-font-btn {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0.25rem;
  padding: 0.5rem; border-radius: 0.5rem; border: 2px solid var(--surface-d, #e5e7eb);
  background: var(--surface-ground, #f8fafc); cursor: pointer; font-weight: 600;
  color: var(--text-color, #1e293b); transition: border-color 0.2s;
}
.ap-font-btn--active { border-color: var(--primary-color, #6366f1); background: color-mix(in srgb, var(--primary-color, #6366f1) 8%, var(--surface-ground, #f8fafc)); }
.ap-font-label { font-size: 0.65rem; }

/* ── Toggle ── */
.ap-toggle { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
.ap-toggle input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.ap-toggle-track {
  width: 36px; height: 20px; border-radius: 99px; background: var(--surface-d, #e5e7eb);
  flex-shrink: 0; position: relative; transition: background 0.25s;
}
.ap-toggle input:checked ~ .ap-toggle-track { background: var(--primary-color, #6366f1); }
.ap-toggle-track::after {
  content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px;
  border-radius: 50%; background: #fff; transition: transform 0.25s;
}
.ap-toggle input:checked ~ .ap-toggle-track::after { transform: translateX(16px); }
.ap-toggle-label { font-size: 0.82rem; color: var(--text-color, #1e293b); }

/* ── Atajos ── */
.ap-shortcuts {}
.ap-shortcut-list { display: flex; flex-direction: column; gap: 0.3rem; }
.ap-shortcut-row { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.ap-shortcut-row dt { display: flex; align-items: center; gap: 0.2rem; }
.ap-shortcut-row dd { font-size: 0.75rem; color: var(--text-color-secondary, #64748b); }
kbd {
  display: inline-flex; align-items: center; padding: 0.1rem 0.35rem;
  font-size: 0.7rem; font-family: monospace; background: var(--surface-d, #e5e7eb);
  border: 1px solid #cbd5e1; border-radius: 0.25rem; color: var(--text-color, #1e293b);
}

/* ── Restablecer ── */
.ap-reset { font-size: 0.78rem; color: var(--text-color-secondary, #64748b); background: none; border: none; cursor: pointer; display: flex; align-items: center; gap: 0.3rem; padding: 0.25rem 0; }
.ap-reset:hover { color: var(--text-color, #1e293b); }

/* ── Transición ── */
.ap-slide-enter-active, .ap-slide-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.ap-slide-enter-from, .ap-slide-leave-to { opacity: 0; transform: translateY(8px); }
</style>
